import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Navbar from "../components/Navbar";
import Globe from "react-globe.gl";
import * as THREE from "three";

// Design system colors (matching CSS variables)
const colors = {
  bgPrimary: "#000",
  bgWhite: "#fff",
  textPrimary: "#fff",
  textWhite90: "rgba(255, 255, 255, 0.9)",
  textWhite50: "rgba(255, 255, 255, 0.5)",
  borderLight: "rgba(255, 255, 255, 0.2)",
  overlayTooltip: "rgba(20, 20, 20, 0.95)",
  overlayLight: "rgba(255, 255, 255, 0.1)",
  shadowTooltip: "0 4px 12px rgba(0, 0, 0, 0.3)",
  shadowSm: "0 2px 5px rgba(0, 0, 0, 0.2)",
  radiusMd: 8,
  radiusLg: 12,
  radiusSm: 5,
};

const GlobePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");
  const [loading, setLoading] = useState(false);
  const globeEl = useRef();
  const [carouselData, setCarouselData] = useState([]);
  const [activeSlide, setActiveSlide] = useState(null);
  const [highlightedLabel, setHighlightedLabel] = useState(null);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [pinScreenPosition, setPinScreenPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem("searchTerm") || ""
  );
  // Keep a reference to all unfiltered data
  const allDataRef = useRef([]);

  const fetchHeroImages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_responses')
        .select('*');

      if (error) throw error;

      // Store the complete dataset
      allDataRef.current = data || [];

      // Apply search filtering if there's a search term
      filterAndSortData(data || [], searchTerm);
    } catch (error) {
      console.error("Error fetching documents: ", error);
    } finally {
      setLoading(false);
    }
  };

  // Separate function to filter and sort data
  const filterAndSortData = (data, term) => {
    if (!term || term.trim() === "") {
      // If no search term, use all data
      const sortedData = [...data];
      sortedData.sort((a, b) => a.legacy_id - b.legacy_id);
      setCarouselData(sortedData);

      const selectedSlide = sortedData.find((item) => item.id == id);
      setActiveSlide(selectedSlide);
      return;
    }

    const searchTermLower = term.toLowerCase().trim();
    const filteredDocs = data.filter((item) => {
      const title = (item.title || "").toLowerCase();
      const country = (item.country || "").toLowerCase();
      const continent = (item.continent || "").toLowerCase();
      return (
        title.includes(searchTermLower) ||
        country.includes(searchTermLower) ||
        continent.includes(searchTermLower)
      );
    });

    filteredDocs.sort((a, b) => a.legacy_id - b.legacy_id);
    setCarouselData(filteredDocs);

    // Find the specific item if id is provided in URL
    const selectedSlide = filteredDocs.find((item) => item.id == id);

    // If we have a specific item from URL, use that
    if (selectedSlide) {
      setActiveSlide(selectedSlide);
    }
    // Otherwise, focus on the first search result that has coordinates
    else if (filteredDocs.length > 0) {
      const firstWithCoordinates = filteredDocs.find(
        (item) => item.latitude && item.longitude
      );
      if (firstWithCoordinates) {
        setActiveSlide(firstWithCoordinates);
      }
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchHeroImages();
  }, []); // Only fetch on initial render

  // Handle URL search params on initial load and changes
  useEffect(() => {
    const searchQuery = searchParams.get("q");
    if (searchQuery !== null) {
      setSearchTerm(searchQuery);
      if (allDataRef.current.length > 0) {
        filterAndSortData(allDataRef.current, searchQuery);
      }
    }
  }, [searchParams]);

  // Update the search handlers to show loading state
  const handleSearchChange = (e) => {
    const newSearchTerm = e.detail.searchTerm;
    setSearchTerm(newSearchTerm);
    setLoading(true);
    if (allDataRef.current.length > 0) {
      filterAndSortData(allDataRef.current, newSearchTerm);
      setTimeout(() => setLoading(false), 300); // Short delay for smoother UI
    }
  };

  // Listen for search term changes from Navbar
  useEffect(() => {
    window.addEventListener("searchTermChanged", handleSearchChange);

    return () => {
      window.removeEventListener("searchTermChanged", handleSearchChange);
    };
  }, []);

  // useEffect(() => {
  //   const globe = globeEl.current;

  //   if (globe && activeSlide && activeSlide.Latitude && activeSlide.Longitude) {
  //     // Stop auto-rotation first
  //     globe.controls().autoRotate = false;

  //     // Move to the point of interest
  //     globe.pointOfView(
  //       { lat: activeSlide.Latitude, lng: activeSlide.Longitude, altitude: 2 },
  //       300
  //     );

  //     setTimeout(() => {
  //       setHighlightedLabel(activeSlide.id);
  //     }, 3000);
  //   } else if (globe) {
  //     globe.controls().autoRotate = true;
  //     globe.controls().autoRotateSpeed = 0.35;
  //   }
  // }, [activeSlide]);

  const labelsData = carouselData
    .filter((item) => item.latitude !== null && item.longitude !== null)
    .map((item) => ({
      id: item.legacy_id,
      firestoreId: item.id, // Store Supabase document ID for navigation
      text: " ",
      lat: item.latitude,
      lng: item.longitude,
      segment: item.segment,
      link: item.link,
      heroImage: item.hero_image || (Array.isArray(item.image_url) && item.image_url.length > 0
        ? (typeof item.image_url[0] === 'object' && item.image_url[0]?.UploadedUrl
          ? item.image_url[0].UploadedUrl
          : (typeof item.image_url[0] === 'string' ? item.image_url[0] : null))
        : (typeof item.image_url === 'string' ? item.image_url : null)) || null,
      Title: item.title,
      Location: item.location,
    }));

  const handlePinHover = (label) => {
    if (label) {
      setHoveredLabel(label);
      // Calculate screen position of the pin
      if (globeEl.current && label.lat && label.lng) {
        try {
          const screenPos = globeEl.current.toScreenPosition(label.lat, label.lng);
          if (screenPos) {
            setPinScreenPosition({ x: screenPos.x, y: screenPos.y });
          }
        } catch (error) {
          console.error("Error calculating screen position:", error);
          // Fallback to default position if calculation fails
          setPinScreenPosition(null);
        }
      }
    } else {
      // Clear hover state when moving away from pin
      setHoveredLabel(null);
      setPinScreenPosition(null);
    }
  };

  const handlePinClick = (label) => {
    // Just set hovered state, navigation will happen on photo click
    setHoveredLabel(label);
    if (globeEl.current && label && label.lat && label.lng) {
      try {
        const screenPos = globeEl.current.toScreenPosition(label.lat, label.lng);
        if (screenPos) {
          setPinScreenPosition({ x: screenPos.x, y: screenPos.y });
        }
      } catch (error) {
        console.error("Error calculating screen position:", error);
        setPinScreenPosition(null);
      }
    }
  };

  const handlePhotoClick = (label) => {
    if (!label || !label.firestoreId) return;

    // Include the search term in the URL if it exists
    const searchParam = searchTerm
      ? `&q=${encodeURIComponent(searchTerm)}`
      : "";
    navigate(`/segments?id=${label.firestoreId}${searchParam}`);
  };

  const latLngToXYZ = (lat, lng, radius = 1) => {
    const latRad = THREE.MathUtils.degToRad(lat);
    const lngRad = THREE.MathUtils.degToRad(lng);

    const x = radius * Math.cos(latRad) * Math.cos(lngRad);
    const y = radius * Math.sin(latRad);
    const z = radius * Math.cos(latRad) * Math.sin(lngRad);

    return new THREE.Vector3(x, y, z);
  };

  const createPinObject = (data) => {
    // Create a group to hold both visible and invisible hit detection spheres
    const group = new THREE.Group();

    // Create larger invisible sphere for hit detection (3x larger)
    const hitDetectionSphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 32, 32), // Larger radius for easier interaction
      new THREE.MeshStandardMaterial({
        visible: false, // Invisible but still registers events
        transparent: true,
        opacity: 0,
      })
    );

    // Create visible small pin (original size)
    const pinColor = data.id === hoveredLabel?.id ? "#44F33E" : "#eeedf5";
    const visiblePin = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 32, 32),
      new THREE.MeshStandardMaterial({
        color: pinColor,
        emissive: pinColor,
        emissiveIntensity: 0.30,
        roughness: 1.0,
        metalness: 0.20,
      })
    );

    // Position both at the same location
    const position = latLngToXYZ(data.lat, data.lng, 0.000000001);
    hitDetectionSphere.position.copy(position);
    visiblePin.position.copy(position);

    // Add both to group
    group.add(hitDetectionSphere);
    group.add(visiblePin);

    // Store data in group for event handling
    group.userData = data;

    return group;
  };

  return (
    <div className="App" style={{ backgroundColor: colors.bgPrimary }}>
      <Navbar />
      <div
        style={{
          height: "100vh",
          paddingTop: "50px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          backgroundColor: colors.bgPrimary,
          boxSizing: "border-box",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(255, 255, 255, 0.9)",
              padding: "10px 20px",
              borderRadius: colors.radiusSm,
              zIndex: 100,
              boxShadow: colors.shadowSm,
            }}
          >
            Searching...
          </div>
        )}
        {searchTerm && carouselData.length === 0 && !loading && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(255, 255, 255, 0.7)",
              padding: "10px 20px",
              borderRadius: colors.radiusSm,
              zIndex: 100,
              boxShadow: colors.shadowSm,
            }}
          >
            No results found for "{searchTerm}"
          </div>
        )}
        <div
          style={{
            maxWidth: "800px",
            display: "flex",
            justifyContent: "center",
            margin: "auto",
          }}
        >
          <Globe
            ref={globeEl}
            backgroundColor="rgba(0, 0, 0, 0)"
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            objectsData={labelsData}
            labelColor={"black"}
            //objectLabel={(d) => d.id}
            objectLat={(d) => d.lat}
            objectLng={(d) => d.lng}
            objectResolution={1}
            onObjectClick={(label) => handlePinClick(label)}
            onObjectHover={(label) => handlePinHover(label)}
            objectThreeObject={createPinObject}
            objectAltitude={0.000001}
          />
        </div>
        {hoveredLabel && (
          <div
            style={{
              position: "absolute",
              top: pinScreenPosition
                ? `${Math.max(40, pinScreenPosition.y - 220)}px`
                : "40px",
              left: pinScreenPosition
                ? `${Math.max(10, Math.min(pinScreenPosition.x + 20, window.innerWidth - 320))}px`
                : "10px",
              background: colors.overlayTooltip,
              padding: "16px",
              borderRadius: colors.radiusLg,
              maxWidth: "300px",
              minWidth: "250px",
              zIndex: 1000,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: colors.shadowTooltip,
              color: colors.textPrimary,
            }}
          >
            {hoveredLabel.heroImage ? (
              <img
                src={hoveredLabel.heroImage}
                alt="Preview Not Available"
                onClick={() => handlePhotoClick(hoveredLabel)}
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  borderRadius: colors.radiusMd,
                  objectFit: "cover",
                  marginBottom: "12px",
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.target.style.opacity = "1")}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "200px",
                  borderRadius: colors.radiusMd,
                  backgroundColor: colors.overlayLight,
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.textWhite50,
                  fontSize: "14px",
                }}
              >
                No Image Available
              </div>
            )}
            <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
              <h7
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "block",
                  color: colors.textPrimary,
                }}
              >
                Title: {hoveredLabel.Title || "N/A"}
              </h7>
              <p
                style={{
                  fontSize: "14px",
                  margin: "0",
                  color: colors.textWhite90,
                  fontWeight: "300",
                }}
              >
                Location: {hoveredLabel.Location || "N/A"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobePage;
